import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { FullReservationData } from '../App';
import { Trash2Icon, ArrowLeftIcon, RefreshCwIcon, ShieldCheckIcon, CreditCardIcon, PackageIcon } from 'lucide-react';
interface CartPageProps {
  onNavigate: (page: string) => void;
  reservationData: FullReservationData | null;
}
export function CartPage({
  onNavigate,
  reservationData
}: CartPageProps) {
  const [quantity, setQuantity] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (!reservationData) {
    return <main className="flex-grow w-full bg-brand-gray min-h-screen flex flex-col" data-id="element-705">
        <PageHeader title="Your Cart" breadcrumb="Cart" onNavigateHome={() => onNavigate('home')} data-id="element-706" />
        <div className="flex-grow flex items-center justify-center p-8" data-id="element-707">
          <div className="text-center bg-white p-12 rounded-3xl shadow-xl max-w-md w-full" data-id="element-708">
            <div className="w-24 h-24 bg-brand-gray rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-709">
              <PackageIcon className="w-10 h-10 text-brand-navy/30" data-id="element-710" />
            </div>
            <h2 className="text-2xl font-bold text-brand-navy mb-4" data-id="element-711">
              Your cart is empty
            </h2>
            <p className="text-brand-charcoal/60 mb-8" data-id="element-712">
              Looks like you haven't added any reservations yet.
            </p>
            <button onClick={() => onNavigate('home')} className="w-full py-4 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg" data-id="element-713">
              Start a Reservation
            </button>
          </div>
        </div>
      </main>;
  }
  const basePrice = parseFloat(reservationData.price.replace(/[^0-9.]/g, '')) || 150.0;
  const subtotal = basePrice * quantity;
  const insuranceFee = 10.0;
  let gelCushionPrice = 0;
  if (reservationData.gelCushion && reservationData.gelCushion !== 'None') {
    gelCushionPrice = reservationData.gelCushion.includes('Premium') ? 15.99 : 5.99;
  }
  const taxRate = 0.06;
  const tax = (subtotal + insuranceFee + gelCushionPrice) * taxRate;
  const total = subtotal + insuranceFee + gelCushionPrice + tax;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-714">
      <PageHeader title="Your Cart" breadcrumb="Cart" onNavigateHome={() => onNavigate('home')} data-id="element-715" />

      <section className="py-12 md:py-20" data-id="element-716">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-717">
          <div className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-718">
            {/* Left Column */}
            <div className="w-full lg:w-2/3 space-y-6" data-id="element-719">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-720">
                {/* Product Header Row */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50" data-id="element-721">
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-722">
                    <div className="w-16 h-16 bg-brand-gray rounded-xl flex items-center justify-center shrink-0 border border-gray-100" data-id="element-723">
                      <PackageIcon className="w-7 h-7 text-brand-navy/40" data-id="element-724" />
                    </div>
                    <div className="min-w-0" data-id="element-725">
                      <h3 className="text-lg font-bold text-brand-navy capitalize truncate" data-id="element-726">
                        {reservationData.equipment || 'Mobility Equipment'}
                      </h3>
                      <p className="text-brand-charcoal/50 text-sm truncate" data-id="element-727">
                        {reservationData.email}
                      </p>
                    </div>
                  </div>

                  {/* Price / Qty / Total / Delete — always visible, aligned right */}
                  <div className="flex items-center gap-6 shrink-0 ml-6" data-id="element-728">
                    <div className="text-right hidden sm:block" data-id="element-729">
                      <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider font-semibold mb-1" data-id="element-730">
                        Price
                      </p>
                      <p className="font-bold text-brand-navy" data-id="element-731">
                        {formatCurrency(basePrice)}
                      </p>
                    </div>
                    <div className="hidden sm:block" data-id="element-732">
                      <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider font-semibold mb-1 text-center" data-id="element-733">
                        Qty
                      </p>
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-734">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-1.5 text-brand-charcoal/60 hover:bg-gray-50 transition-colors text-sm" data-id="element-735">
                          −
                        </button>
                        <span className="w-8 text-center font-bold text-brand-navy text-sm" data-id="element-736">
                          {quantity}
                        </span>
                        <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-1.5 text-brand-charcoal/60 hover:bg-gray-50 transition-colors text-sm" data-id="element-737">
                          +
                        </button>
                      </div>
                    </div>
                    <div className="text-right hidden sm:block" data-id="element-738">
                      <p className="text-xs text-brand-charcoal/50 uppercase tracking-wider font-semibold mb-1" data-id="element-739">
                        Total
                      </p>
                      <p className="font-bold text-brand-navy" data-id="element-740">
                        {formatCurrency(subtotal)}
                      </p>
                    </div>
                    <button onClick={() => {
                    if (confirm('Remove this reservation?')) onNavigate('home');
                  }} className="p-2 text-gray-300 hover:text-brand-red hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove item" data-id="element-741">
                      <Trash2Icon className="w-4 h-4" data-id="element-742" />
                    </button>
                  </div>
                </div>

                {/* Mobile Price Row */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 sm:hidden" data-id="element-743">
                  <div data-id="element-744">
                    <span className="text-xs text-brand-charcoal/50 uppercase tracking-wider font-semibold" data-id="element-745">
                      Price:{' '}
                    </span>
                    <span className="font-bold text-brand-navy" data-id="element-746">
                      {formatCurrency(basePrice)}
                    </span>
                  </div>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-747">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-1.5 text-brand-charcoal/60 hover:bg-gray-50 transition-colors text-sm" data-id="element-748">
                      −
                    </button>
                    <span className="w-8 text-center font-bold text-brand-navy text-sm" data-id="element-749">
                      {quantity}
                    </span>
                    <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-1.5 text-brand-charcoal/60 hover:bg-gray-50 transition-colors text-sm" data-id="element-750">
                      +
                    </button>
                  </div>
                  <div data-id="element-751">
                    <span className="text-xs text-brand-charcoal/50 uppercase tracking-wider font-semibold" data-id="element-752">
                      Total:{' '}
                    </span>
                    <span className="font-bold text-brand-navy" data-id="element-753">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                </div>

                {/* Reservation Details */}
                <div className="p-6" data-id="element-754">
                  <div className="inline-flex items-center gap-2 bg-brand-red/10 text-brand-red px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5" data-id="element-755">
                    Reservation Details
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-id="element-756">
                    <div className="flex items-baseline gap-2" data-id="element-757">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-758">
                        Name:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-759">
                        {reservationData.firstName} {reservationData.lastName}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-760">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-761">
                        Email:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-762">
                        {reservationData.email}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-763">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-764">
                        Phone:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-765">
                        {reservationData.phoneNumber}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-766">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-767">
                        Rental Type:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-768">
                        {reservationData.rentalType}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-769">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-770">
                        Location:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-771">
                        {reservationData.deliveryLocation}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-772">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-773">
                        Equipment:
                      </span>
                      <span className="text-brand-navy font-semibold capitalize" data-id="element-774">
                        {reservationData.equipment}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-775">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-776">
                        Rental Date:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-777">
                        {reservationData.rentalDate}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-778">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-779">
                        Return Date:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-780">
                        {reservationData.returnDate}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-781">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-782">
                        Weight:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-783">
                        {reservationData.weight}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-784">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-785">
                        Height:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-786">
                        {reservationData.height}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-787">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-788">
                        Place Name:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-789">
                        {reservationData.placeName}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-790">
                      <span className="text-brand-charcoal/50 font-medium shrink-0 w-28" data-id="element-791">
                        Place Address:
                      </span>
                      <span className="text-brand-navy font-semibold" data-id="element-792">
                        {reservationData.placeAddress}
                      </span>
                    </div>
                  </div>

                  {/* Message */}
                  {reservationData.message && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-793">
                      <span className="text-brand-charcoal/50 font-medium text-sm" data-id="element-794">
                        Message:
                      </span>
                      <p className="text-brand-navy text-sm mt-1 bg-brand-gray p-3 rounded-xl italic" data-id="element-795">
                        {reservationData.message}
                      </p>
                    </div>}

                  {/* Accessories */}
                  {gelCushionPrice > 0 && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-796">
                      <h4 className="font-bold text-brand-navy text-sm mb-2" data-id="element-797">
                        Rental Accessories:
                      </h4>
                      <div className="flex justify-between items-center bg-brand-gray px-4 py-2.5 rounded-xl text-sm" data-id="element-798">
                        <span className="font-medium" data-id="element-799">
                          {reservationData.gelCushion} (x1)
                        </span>
                        <span className="text-brand-red font-bold" data-id="element-800">
                          {formatCurrency(gelCushionPrice)}
                        </span>
                      </div>
                    </div>}
                </div>
              </motion.div>

              {/* Cart Actions */}
              <div className="flex justify-between items-center px-1" data-id="element-801">
                <button onClick={() => onNavigate('home')} className="flex items-center gap-2 text-brand-charcoal/50 hover:text-brand-navy font-semibold text-sm transition-colors group" data-id="element-802">
                  <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-803" />
                  Continue Shopping
                </button>
                <button className="flex items-center gap-2 text-brand-charcoal/50 hover:text-brand-navy font-semibold text-sm transition-colors" data-id="element-804">
                  <RefreshCwIcon className="w-4 h-4" data-id="element-805" />
                  Update Cart
                </button>
              </div>

              {/* Coupon */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-806">
                <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-807">
                  Coupon Apply
                </h3>
                <div className="flex gap-3" data-id="element-808">
                  <input type="text" placeholder="Enter coupon code here..." value={couponCode} onChange={e => setCouponCode(e.target.value)} className="flex-grow bg-brand-gray border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all" data-id="element-809" />
                  <button className="bg-brand-red text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-red-700 transition-colors shadow-sm whitespace-nowrap" data-id="element-810">
                    Apply
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Cart Totals */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className="w-full lg:w-1/3" data-id="element-811">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-28" data-id="element-812">
                <div className="p-6 border-b border-gray-100" data-id="element-813">
                  <h3 className="text-xl font-bold text-brand-navy" data-id="element-814">
                    Cart Totals
                  </h3>
                </div>

                <div className="p-6 space-y-4" data-id="element-815">
                  <div className="flex justify-between items-center text-sm" data-id="element-816">
                    <span className="text-brand-charcoal/60" data-id="element-817">Subtotal</span>
                    <span className="font-bold text-brand-navy" data-id="element-818">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm" data-id="element-819">
                    <span className="text-brand-charcoal/60" data-id="element-820">
                      Total Insurance Fee (x1)
                    </span>
                    <span className="font-bold text-brand-navy" data-id="element-821">
                      {formatCurrency(insuranceFee)}
                    </span>
                  </div>
                  {gelCushionPrice > 0 && <div className="flex justify-between items-center text-sm" data-id="element-822">
                      <span className="text-brand-charcoal/60" data-id="element-823">
                        Gel Cushion
                      </span>
                      <span className="font-bold text-brand-navy" data-id="element-824">
                        {formatCurrency(gelCushionPrice)}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center text-sm pb-4 border-b border-gray-100" data-id="element-825">
                    <span className="text-brand-charcoal/60" data-id="element-826">Tax (6%)</span>
                    <span className="font-bold text-brand-navy" data-id="element-827">
                      {formatCurrency(tax)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2" data-id="element-828">
                    <span className="text-base font-bold text-brand-navy" data-id="element-829">
                      Total
                    </span>
                    <span className="text-2xl font-extrabold text-brand-red" data-id="element-830">
                      {formatCurrency(total)}
                    </span>
                  </div>

                  <button onClick={() => onNavigate('checkout')} className="w-full py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2 mt-8" data-id="element-831">
                    <CreditCardIcon className="w-5 h-5" data-id="element-832" />
                    Proceed To Checkout
                  </button>

                  <div className="flex items-center justify-center gap-2 text-brand-charcoal/50 text-xs mt-6" data-id="element-833">
                    <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-834" />
                    <span data-id="element-835">Secure encrypted checkout</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>;
}